import React, { useEffect } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BrandMark } from '../../components/ui/BrandMark';
import { GlassButton } from '../../components/ui/GlassButton';
import { GlassCard } from '../../components/ui/GlassCard';
import { APP_NAME } from '../../config/branding';
import { useAuth } from '../../contexts/AuthContext';
import {
  navigateToLegal,
  openDashboard,
  openWebAppSignIn,
} from '../../core/navigation/navigationRef';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { consumeBillingReturnPending } from '../../services/billingService';
import { PRO_FEATURES } from './landingCopy';
import { MarketingBackBar } from './MarketingBackBar';

/**
 * Post-purchase confirmation — Stripe success_url and the public
 * “thank you” URL for Ads / Play / store consoles (`/thanks`).
 */
export function ThankYouScreen() {
  const insets = useSafeAreaInsets();
  const { isAuthenticated, refreshUser, user } = useAuth();
  const isPro = user?.plan === 'pro';

  useEffect(() => {
    consumeBillingReturnPending();
    if (!isAuthenticated) return;
    void refreshUser().catch(() => undefined);
  }, [isAuthenticated, refreshUser]);

  const onContinue = () => {
    if (isAuthenticated) {
      openDashboard();
      return;
    }
    openWebAppSignIn();
  };

  return (
    <View style={styles.root}>
      <MarketingBackBar title="Thank you" />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: Math.max(insets.bottom, spacing.xl) + spacing.xl },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.hero} accessibilityRole="header">
          <BrandMark size={56} />
          <View style={styles.check} accessibilityElementsHidden>
            <View style={styles.checkBar} />
          </View>
          <Text style={styles.kicker}>{APP_NAME} Pro</Text>
          <Text style={styles.title}>Thank you</Text>
          <Text style={styles.body}>
            {isPro
              ? `You’re on ${APP_NAME} Pro. A receipt is on its way to the email on this account.`
              : `Your purchase is confirmed. Pro unlocks on this account in a moment — then open the dashboard.`}
          </Text>
        </View>

        <GlassCard style={styles.card}>
          <Text style={styles.cardTitle}>What’s included</Text>
          {PRO_FEATURES.map((line) => (
            <View key={line} style={styles.featureRow}>
              <Text style={styles.featureDot}>•</Text>
              <Text style={styles.feature}>{line}</Text>
            </View>
          ))}
        </GlassCard>

        <View style={styles.actions}>
          <GlassButton variant="primary" fullWidth onPress={onContinue}>
            {isAuthenticated ? 'Open dashboard' : 'Sign in to open Pro'}
          </GlassButton>
          <GlassButton variant="ghost" fullWidth onPress={() => navigateToLegal('Support')}>
            Need help?
          </GlassButton>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroll: {
    flex: 1,
  },
  content: {
    paddingTop: spacing.xl,
    paddingHorizontal: spacing.lg,
    maxWidth: 520,
    width: '100%',
    alignSelf: 'center',
    gap: spacing.lg,
  },
  hero: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  check: {
    width: 36,
    height: 36,
    borderRadius: radii.pill,
    backgroundColor: 'rgba(61, 214, 164, 0.16)',
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.sm,
  },
  checkBar: {
    width: 14,
    height: 7,
    borderLeftWidth: 2,
    borderBottomWidth: 2,
    borderColor: colors.accent,
    transform: [{ rotate: '-45deg' }, { translateY: -1 }],
  },
  kicker: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginTop: spacing.sm,
  },
  title: {
    ...typography.title,
    color: colors.text,
    textAlign: 'center',
  },
  body: {
    ...typography.body,
    color: colors.textMuted,
    textAlign: 'center',
    lineHeight: 24,
    maxWidth: 400,
  },
  card: {
    gap: spacing.sm,
  },
  cardTitle: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  featureDot: {
    ...typography.body,
    color: colors.accent,
    lineHeight: 24,
    fontWeight: '700',
  },
  feature: {
    ...typography.body,
    color: colors.text,
    lineHeight: 24,
    flex: 1,
  },
  actions: {
    gap: spacing.sm,
  },
});
